import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faSearch,
  faFilter,
  faBoxOpen,
  faHeart,
  faShoppingCart,
  faStore,
  faSortAmountDown,
  faFlask,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import ProductCard from "./components/ProductCard";
import productsData from "./fallback/products";
import { useHerKart } from "./hooks/useHerKart";

const HerKartProducts = () => {
  const navigate = useNavigate();

  const {
    fetchCart,
    syncCart,
    fetchSaved,
    syncSaved,
    fetchLiveProducts,
    fetchStoreByCode,
  } = useHerKart();
  const { storeCode: urlStoreCode } = useParams();

  const [liveProducts, setLiveProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showSamples, setShowSamples] = useState(false);

  const sampleProducts = useMemo(
    () => productsData.filter(Boolean).map((p) => ({ ...p, isSample: true })),
    [],
  );

  // Live products first; sample/fallback products appended only when toggled on.
  const products = useMemo(() => {
    return showSamples ? [...liveProducts, ...sampleProducts] : liveProducts;
  }, [liveProducts, sampleProducts, showSamples]);

  const loadLiveProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetchLiveProducts();
      if (response?.success) {
        setLiveProducts(
          (response.products || []).map((p) => ({ ...p, isSample: false })),
        );
      }
    } catch (e) {
      console.error("Products fetch error:", e);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadLiveProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [storeInfo, setStoreInfo] = useState(null);
  const [storeCheckLoading, setStoreCheckLoading] = useState(!!urlStoreCode);
  const [storeNotFound, setStoreNotFound] = useState(false);

  const [isSpeakingStoreTTS, setIsSpeakingStoreTTS] = useState(false);

  const handleToggleSpeakStoreTTS = () => {
    if (!storeInfo?.tts || !("speechSynthesis" in window)) return;

    if (isSpeakingStoreTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingStoreTTS(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(storeInfo.tts);
    utterance.lang = "en-IN";
    utterance.onend = () => setIsSpeakingStoreTTS(false);
    utterance.onerror = () => setIsSpeakingStoreTTS(false);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingStoreTTS(true);
  };

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const [showPrivateStoreFilter, setShowPrivateStoreFilter] = useState(false);
  const [manualStoreCodeInput, setManualStoreCodeInput] = useState("");
  const [manualStoreCode, setManualStoreCode] = useState("");

  useEffect(() => {
    if (!urlStoreCode) return;

    const checkStore = async () => {
      setStoreCheckLoading(true);
      setStoreNotFound(false);
      try {
        const response = await fetchStoreByCode(urlStoreCode);
        if (response?.success && response.store) {
          setStoreInfo(response.store);
        } else {
          setStoreNotFound(true);
        }
      } catch (e) {
        console.error("Store lookup error:", e);
        setStoreNotFound(true);
      } finally {
        setStoreCheckLoading(false);
      }
    };

    checkStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStoreCode]);

  const effectiveStoreCode =
    urlStoreCode || (showPrivateStoreFilter ? manualStoreCode : "");

  const [search, setSearch] = useState("");

  const [selectedCategory, setSelectedCategory] = useState("All");

  const [sortBy, setSortBy] = useState("featured");
  const [cart, setCart] = useState({});
  const [savedIds, setSavedIds] = useState([]);
  const [loadingKart, setLoadingKart] = useState(true);

  // Serialized write queues — guarantee cart/saved updates persist in the
  // exact order they were triggered, instead of racing independent requests.
  const cartQueueRef = useRef(Promise.resolve());
  const savedQueueRef = useRef(Promise.resolve());

  const buildCartItems = (cartMap) => {
    return Object.entries(cartMap).map(([productId, quantity]) => {
      const p = products.find((item) => String(item._id) === String(productId));
      return {
        productId,
        name: p?.name || "",
        store: p?.store || "",
        entrepreneur: p?.entrepreneur || "",
        city: p?.city || "",
        price: p?.sellingPrice || 0,
        quantity,
      };
    });
  };

  const buildSavedItems = (ids) => {
    return ids.map((productId) => {
      const p = products.find((item) => String(item._id) === String(productId));
      return {
        productId,
        name: p?.name || "",
        store: p?.store || "",
        entrepreneur: p?.entrepreneur || "",
        city: p?.city || "",
        price: p?.sellingPrice || 0,
        quantity: 1,
      };
    });
  };

  const persistCart = (nextCartMap) => {
    const items = buildCartItems(nextCartMap);
    cartQueueRef.current = cartQueueRef.current
      .then(() => syncCart(items))
      .catch((e) => console.error("Cart sync error:", e));
  };

  const persistSaved = (nextIds) => {
    const items = buildSavedItems(nextIds);
    savedQueueRef.current = savedQueueRef.current
      .then(() => syncSaved(items))
      .catch((e) => console.error("Saved sync error:", e));
  };

  const loadMyKart = async () => {
    setLoadingKart(true);
    try {
      const [cartRes, savedRes] = await Promise.all([
        fetchCart(),
        fetchSaved(),
      ]);

      if (cartRes?.success) {
        const cartMap = {};
        (cartRes.items || []).forEach((item) => {
          cartMap[item.productId] = item.quantity;
        });
        setCart(cartMap);
      }

      if (savedRes?.success) {
        setSavedIds((savedRes.items || []).map((item) => item.productId));
      }
    } catch (e) {
      console.error("Kart fetch error:", e);
    } finally {
      setLoadingKart(false);
    }
  };

  useEffect(() => {
    loadMyKart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => {
    const unique = [
      ...new Set(products.map((item) => item.category).filter(Boolean)),
    ];

    return ["All", ...unique];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = products.filter(Boolean);

    if (effectiveStoreCode) {
      list = list.filter((item) => item.storeCode === effectiveStoreCode);
    }

    if (selectedCategory !== "All") {
      list = list.filter((item) => item.category === selectedCategory);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();

      list = list.filter((item) => {
        return [
          item.name,
          item.category,
          item.store,
          item.entrepreneur,
          item.city,
          item.locality,
        ]
          .join(" ")
          .toLowerCase()
          .includes(keyword);
      });
    }

    if (effectiveStoreCode) {
      // Store-driven landing: Innovation first, then Brand, then Pilot Sample, then the rest.
      const rank = (p) =>
        p.isInnovation ? 0 : p.isBrand ? 1 : p.isPilotSample ? 2 : 3;
      list = [...list].sort((a, b) => rank(a) - rank(b));
    } else {
      switch (sortBy) {
        case "name":
          list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          break;

        case "nameReverse":
          list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
          break;

        case "priceLow":
          list.sort((a, b) => (a.sellingPrice || 0) - (b.sellingPrice || 0));
          break;

        case "priceHigh":
          list.sort((a, b) => (b.sellingPrice || 0) - (a.sellingPrice || 0));
          break;
        
        case "locality":
          list.sort((a, b) => (a.locality || "").localeCompare(b.locality || ""));
          break;

        case "city":
          list.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
          break;

        default:
          break;
      }
    }

    return list;
  }, [products, search, selectedCategory, sortBy, effectiveStoreCode]);

  const totalCartItems = useMemo(() => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  }, [cart]);

  const totalSaved = savedIds.length;

  const handleAdd = (product) => {
    if (product.isSample) return;
    setCart((prev) => {
      const next = {
        ...prev,
        [product._id]: (prev[product._id] || 0) + 1,
      };
      persistCart(next);
      return next;
    });
  };

  const handleIncrease = (product) => {
    if (product.isSample) return;
    setCart((prev) => {
      const next = {
        ...prev,
        [product._id]: (prev[product._id] || 0) + 1,
      };
      persistCart(next);
      return next;
    });
  };

  const handleDecrease = (product) => {
    if (product.isSample) return;
    setCart((prev) => {
      const qty = prev[product._id] || 0;
      let next;

      if (qty <= 1) {
        next = { ...prev };
        delete next[product._id];
      } else {
        next = { ...prev, [product._id]: qty - 1 };
      }

      persistCart(next);
      return next;
    });
  };

  const handleSave = (product) => {
    if (product.isSample) return;
    setSavedIds((prev) => {
      const next = prev.includes(product._id)
        ? prev.filter((id) => id !== product._id)
        : [...prev, product._id];

      persistSaved(next);
      return next;
    });
  };

  const handleView = (product) => {
    navigate(`/herkart/product/${product._id}`);
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex flex-col max-w-7xl px-4 py-2">
          <div className="flex items-center gap-3">
            <Link
              to="/foundher"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl font-bold">Her Products</h1>

              <p className="text-xs text-slate-400">
                Discover women-led brands
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`rounded-xl px-2 py-2 text-xs font-semibold ${
                showSamples
                  ? "bg-amber-500/20 text-amber-300"
                  : "bg-emerald-500/20 text-emerald-300"
              }`}
            >
              {showSamples ? "Dummy Products" : "Live Products"}
            </span>

            <button
              onClick={() => setShowSamples((prev) => !prev)}
              className={`flex items-center gap-2 rounded-xl w-fit px-2 py-1 text-xs ${
                showSamples
                  ? "bg-white/10 hover:bg-white/20"
                  : "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
              }`}
              title="Toggle between live and dummy products"
            >
              <FontAwesomeIcon icon={faFlask} />
              {showSamples ? "Show Live" : "Show Dummy"}
            </button>

            <Link
              to="/herkart/saved"
              className="flex items-center gap-2 rounded-xl bg-pink-600/20 w-fit px-2 py-1 text-pink-300 hover:bg-pink-600/30"
            >
              <FontAwesomeIcon icon={faHeart} />
              <span className="text-sm font-semibold">{totalSaved}</span>
            </Link>

            <button
              onClick={() => navigate("/herkart/cart")}
              className="flex items-center gap-2 rounded-xl bg-pink-600 w-fit px-2 py-1 text-sm font-semibold text-white hover:bg-pink-500"
            >
              <FontAwesomeIcon icon={faShoppingCart} />
              Cart ({totalCartItems})
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-2"></div>
      <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products, stores, entrepreneurs, cities..."
            className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-pink-500 focus:outline-none"
          />
        </div>

        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <div className="flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-2 text-xs text-pink-300 whitespace-nowrap">
            <FontAwesomeIcon icon={faFilter} />
            Categories
          </div>

          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`whitespace-nowrap rounded-full w-fit px-1 py-1 text-xs lg:text-sm transition ${
                selectedCategory === category
                  ? "bg-pink-600 text-white"
                  : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {!urlStoreCode && (
          <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center">
            <button
              type="button"
              onClick={() => {
                setShowPrivateStoreFilter((prev) => !prev);
                if (showPrivateStoreFilter) {
                  setManualStoreCode("");
                  setManualStoreCodeInput("");
                }
              }}
              className={`flex items-center gap-2 rounded-xl w-fit px-2 py-1 text-xs ${
                showPrivateStoreFilter
                  ? "bg-pink-600/20 text-pink-300"
                  : "bg-white/10 hover:bg-white/20"
              }`}
            >
              <FontAwesomeIcon icon={faStore} />
              {showPrivateStoreFilter
                ? "Hide Private Store"
                : "Show Private Store"}
            </button>

            {showPrivateStoreFilter && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={manualStoreCodeInput}
                  onChange={(e) => setManualStoreCodeInput(e.target.value)}
                  placeholder="Enter Store Code"
                  className="rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-sm text-white outline-none focus:border-pink-500"
                />
                <button
                  type="button"
                  onClick={() =>
                    setManualStoreCode(manualStoreCodeInput.trim())
                  }
                  className="w-fit px-2 py-1 rounded-xl bg-pink-600 text-xs font-semibold hover:bg-pink-500"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-2 flex flex-row gap-3 justify-end">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <FontAwesomeIcon
              icon={faSortAmountDown}
              className="text-pink-400"
            />

            <span>Sort By</span>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            disabled={!!effectiveStoreCode}
            className="rounded-xl border border-white/10 bg-[#0b1120] w-fit px-2 py-1 text-sm text-white focus:border-pink-500 focus:outline-none disabled:opacity-50"
          >
            <option value="featured">Featured</option>
            <option value="name">Name (A-Z)</option>
            <option value="nameReverse">Name (Z-A)</option>
            <option value="priceLow">Price (Low-High)</option>
            <option value="priceHigh">Price (High-Low)</option>
            <option value="locality">Area/Neighbourhood</option>
            <option value="city">City</option>
          </select>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-pink-500/15 to-pink-500/5 p-4">
          <div className="flex items-center gap-2 text-pink-300">
            <FontAwesomeIcon icon={faBoxOpen} />
            <span className="text-xs uppercase tracking-wide">Products</span>
          </div>

          <div className="mt-2 text-2xl font-bold">
            {filteredProducts.length}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/15 to-violet-500/5 p-4">
          <div className="flex items-center gap-2 text-violet-300">
            <FontAwesomeIcon icon={faStore} />
            <span className="text-xs uppercase tracking-wide">Stores</span>
          </div>

          <div className="mt-2 text-2xl font-bold">
            {new Set(filteredProducts.map((p) => p.store)).size}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 p-4">
          <div className="flex items-center gap-2 text-emerald-300">
            <FontAwesomeIcon icon={faShoppingCart} />
            <span className="text-xs uppercase tracking-wide">Cart</span>
          </div>

          <div className="mt-2 text-2xl font-bold">{totalCartItems}</div>
        </div>
      </div>

      {urlStoreCode && storeInfo && (
        <div className="mt-6 rounded-2xl border border-pink-500/30 bg-pink-500/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-pink-200">
                Viewing private store:{" "}
                <span className="font-semibold">{storeInfo.storeName}</span> (
                {storeInfo.storeCode})
              </p>
              {storeInfo.storeHeadline && (
                <p className="mt-1 text-xs text-pink-300">
                  {storeInfo.storeHeadline}
                </p>
              )}
            </div>

            {storeInfo.tts && (
              <button
                type="button"
                onClick={handleToggleSpeakStoreTTS}
                title={isSpeakingStoreTTS ? "Stop" : "Listen to store introduction"}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all ${
                  isSpeakingStoreTTS
                    ? "bg-pink-500 text-white"
                    : "bg-white/10 text-pink-300 hover:bg-white/20"
                }`}
              >
                <FontAwesomeIcon icon={isSpeakingStoreTTS ? faVolumeXmark : faVolumeHigh} />
              </button>
            )}
          </div>
        </div>
      )}

      {urlStoreCode && storeCheckLoading ? (
        <div className="mt-16 text-center text-slate-400">
          Checking store...
        </div>
      ) : urlStoreCode && storeNotFound ? (
        <div className="mt-16 rounded-3xl border border-dashed border-red-500/30 bg-red-500/5 p-10 text-center">
          <FontAwesomeIcon
            icon={faStore}
            className="mx-auto mb-4 text-4xl text-red-400"
          />
          <h3 className="text-xl font-semibold">Store not found</h3>
          <p className="mt-2 text-sm text-slate-400">
            No active store exists with the code "{urlStoreCode}".
          </p>
          <Link
            to="/herkart/products"
            className="mt-5 inline-block rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold hover:bg-pink-500"
          >
            Browse All Products
          </Link>
        </div>
      ) : loadingProducts ? (
        <div className="mt-16 text-center text-slate-400">
          Loading products...
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {filteredProducts.map((product) => (
            <div key={product._id} className="relative">
              {product.isSample && (
                <span className="absolute left-3 top-3 z-10 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-black shadow-lg">
                  Sample — Not for Sale
                </span>
              )}
              <ProductCard
                product={product}
                quantity={cart[product._id] || 0}
                isSaved={savedIds.includes(product._id)}
                onAdd={handleAdd}
                onIncrease={handleIncrease}
                onDecrease={handleDecrease}
                onToggleSave={handleSave}
                onView={handleView}
              />
            </div>
          ))}
        </div>
      )}

      {!loadingProducts && !storeNotFound && filteredProducts.length === 0 && (
        <div className="mt-16 rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
          <FontAwesomeIcon
            icon={faBoxOpen}
            className="mx-auto mb-4 text-4xl text-slate-500"
          />

          <h3 className="text-yellow-100 text-sm lg:text-xl font-semibold">No products found</h3>

          <p className="mt-2 text-sm text-slate-400">
            Try changing your search or category or store code.
          </p>
        </div>
      )}
    </div>
  );
};

export default HerKartProducts;
